/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { PaymentAccountType } from '../enums/paymentaccounttype.enum';
import type { MatDatepicker } from '@angular/material/datepicker';

@Injectable({
  providedIn: 'root',
})
/**
 * Provides shared utility helpers for null checks, logging, UI spinner handling,
 * viewport helpers, and payment card brand detection.
 */
export class UtilService {
  isLoading = false;
  showLoadingMessage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  updateNav = signal(false);
  isSpinnerOnPage = signal(false);
  plannedMaintenance = signal(false);
  hasLoggedIn = signal(true);
  rateChangeOrUpdate = signal(false);

  /**
   * Checks whether a string is null, undefined, or only whitespace.
   * @param str - The string to evaluate
   * @returns boolean - true when the input is null/undefined/whitespace, otherwise false
   */
  isNullOrWhitespace(str: string | null | undefined): boolean {
    return str === null || str === undefined || str.trim() === '';
  }

  /**
   * Checks whether a value is null or undefined.
   * @param value - The value to evaluate
   * @returns boolean - true when the input is null or undefined, otherwise false
   */
  isNullOrUndefined(value: any): boolean {
    return value === null || value === undefined;
  }

  /**
   * Logs a collapsed console group when environment logging is enabled.
   * @param title - The group title
   * @param msg - The message payload to log within the group
   */
  consoleGroup(title: string, msg: any): void {
    if (environment.enableLogging) {
      console.groupCollapsed(title);
      console.log(msg);
      console.groupEnd();
    }
  }

  /**
   * Logs a message to the console when environment logging is enabled.
   * @param msg - The message payload to log
   */
  consoleLog(msg: any): void {
    if (environment.enableLogging) {
      console.log(msg);
    }
  }

  /**
   * Displays the global spinner and optionally enables the loading message state.
   * @param showMessage - Whether to emit a loading message state (default: false)
   */
  showSpinner(showMessage = false): void {
    if (typeof document !== 'undefined') {
      this.isSpinnerOnPage.set(true);
      const spinner = document.getElementById('spinner');
      spinner?.classList.add('show');
    }
    this.isLoading = true;
    if (showMessage) {
      this.showLoadingMessage.next(true);
    }
  }

  /**
   * Hides the global spinner and resets loading state/message indicators.
   */
  hideSpinner(): void {
    if (typeof document !== 'undefined') {
      const spinner = document.getElementById('spinner');
      spinner?.classList.remove('show');
      this.isLoading = false;
      this.isSpinnerOnPage.set(false);
      this.showLoadingMessage.next(false);
    }
  }

  /**
   * Function to check if screen width is lower than passed in width.
   * @param mobileWidth maximum width to be considered mobile(non-inclusive)
   * @returns true if screen size is smaller than mobileWidth, false otherwise
   */
  isMobile(mobileWidth: number): boolean {
    if (typeof window !== 'undefined') {
      return window.innerWidth < mobileWidth;
    } else {
      return false;
    }
  }

  // https://stackoverflow.com/a/21617574
  /**
   * Detects the credit card brand from a partial or full card number.
   * @param currVal - Raw card number input, may include non-digit characters
   * @returns PaymentAccountType - The detected card brand enum, or Unknown when no match is found
   */
  getCreditCardBrandFromNumber(currVal: string): PaymentAccountType {
    // American Express
    const amexRegex = /^3[47][0-9]{0,}$/; // 34, 37
    // Visa
    const visaRegex = /^4[0-9]{0,}$/; // 4
    // MasterCard
    const mastercardRegex = /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)[0-9]{0,}$/; // 2221-2720, 51-55
    const maestroRegex = /^(5[06789]|6)[0-9]{0,}$/; // always growing in the range: 60-69, started with / not something else, but starting 5 must be encoded as mastercard anyway
    // Discover
    const discoverRegex = /^(6011|65|64[4-9]|62212[6-9]|6221[3-9]|622[2-8]|6229[01]|62292[0-5])[0-9]{0,}$/; // 6011, 622126-622925, 644-649, 65

    // get rid of anything but numbers
    currVal = currVal.replace(/\D/g, '');

    // checks per each, as their could be multiple hits
    // fix: ordering matter in detection, otherwise can give false results in rare cases
    let selBrand = PaymentAccountType.Unknown;
    if (currVal.match(amexRegex)) {
      selBrand = PaymentAccountType.AmericanExpress;
    } else if (currVal.match(visaRegex)) {
      selBrand = PaymentAccountType.Visa;
    } else if (currVal.match(mastercardRegex)) {
      selBrand = PaymentAccountType.MasterCard;
    } else if (currVal.match(discoverRegex)) {
      selBrand = PaymentAccountType.Discover;
    } else if (currVal.match(maestroRegex)) {
      if (currVal[0] === '5') {
        // started 5 must be mastercard
        selBrand = PaymentAccountType.MasterCard;
      } else {
        selBrand = PaymentAccountType.Unknown; // maestro is all 60-69 which is not something else, thats why this condition in the end
      }
    }

    return selBrand;
  }

  /**
   * Calculates the height an element should be to reach the bottom of page
   * @param el (element) The element you want to set the height of
   * @param heightFromBottom (number) How far from the bottom should element be
   */
  setElementHeight(el: any = null, heightFromBottom = 0): void {
    if (!el) {
      return;
    }
    // distance of element from top https://stackoverflow.com/a/50310297
    const topOfScollContent = window.scrollY + el.getBoundingClientRect().top;
    const scrollContentHeight = window.innerHeight - (topOfScollContent + heightFromBottom);
    el.style.height = scrollContentHeight + 'px';
  }

  // https://stackoverflow.com/a/21627295
  /**
   * Determines whether an element is visible within the current viewport,
   * including checks against scrollable parent containers.
   * @param el - The element to evaluate for visibility
   * @returns boolean - true when visible in viewport, otherwise false
   */
  elementIsVisible(el: any): boolean {
    let rect = el.getBoundingClientRect();
    const top = rect.top;
    const height = rect.height;
    el = el.parentNode;
    // Check if bottom of the element is off the page
    if (rect.bottom < 0) {
      return false;
    }
    // Check its within the document viewport
    if (top > document.documentElement.clientHeight) {
      return false;
    }
    do {
      rect = el.getBoundingClientRect();
      if (!(top <= rect.bottom)) {
        return false;
      }
      // Check if the element is out of view due to a container scrolling
      if (top + height <= rect.top) {
        return false;
      }
      el = el.parentNode;
    } while (el !== document.body);
    return true;
  }

  /**
   * Attaches a resize listener that closes the datepicker when page zoom changes.
   * Call this when datepicker opens and call the returned cleanup function when it closes.
   * @param datepicker - The MatDatepicker instance to close on zoom
   * @returns A cleanup function to remove the resize listener
   */
  attachDatepickerZoomListener<D>(datepicker: MatDatepicker<D>): () => void {
    if (typeof window === 'undefined' || !datepicker) {
      return () => {};
    }

    let lastPixelRatio = window.devicePixelRatio;

    const handleResize = (): void => {
      const currentPixelRatio = window.devicePixelRatio;
      if (currentPixelRatio !== lastPixelRatio) {
        datepicker.close();
        lastPixelRatio = currentPixelRatio;
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }
}
